<div class="wrapper">
  <div class="action-section flex flex-row-reverse my-[20px] w-full gap-[20px]">
    @if (canCreate) {
      <app-primary-button (click)="create()" class="!w-[150px]" data-testid="mgvw-create"> Create </app-primary-button>
    }
    <div class="download-button-container">
      @if (exporting) {
        <mat-spinner
          color="primary"
          diameter="24"
          mode="indeterminate"
          strokeWidth="3"
          >
        </mat-spinner>
      }
      @if (!exporting) {
        <button
          (click)="exportXls()"
          [disabled]="!allRecords.length"
          class="download-button"
          data-testid="mgvw-export-xls"
          mat-icon-button
          matTooltip="Download MGVW report (XLS)"
          >
          <mat-icon>download</mat-icon>
        </button>
      }
    </div>
    <div class="mgvw-filters flex flex-row gap-[20px] flex-1 mt-[7px]">
      <div class="search-section flex flex-1 flex-row gap-[10px] min-w-0">
        <form [formGroup]="searchForm" class="flex-1 min-w-0">
          <div class="search w-full">
            <mat-icon svgIcon="search"></mat-icon>
            <input
              class="search-input"
              data-testid="mgvw-search"
              formControlName="searchText"
              matInput
              placeholder="Search"
              />
            @if (searchForm?.controls?.searchText?.value) {
              <mat-icon (click)="clearSearch()" svgIcon="close"></mat-icon>
            }
          </div>
        </form>
      </div>
    </div>
  </div>

  <div class="list-section">
    <div class="mgvw-list-table flex flex-col gap-[40px]">
      @if (loading && !allRecords.length) {
        <div class="no-data text-type-8 mt-[80px]">Loading MGVW records&hellip;</div>
      }

      @if (!loading && loadError) {
        <div class="no-data text-type-8 mt-[80px]">{{ loadError }}</div>
      }

      @if (!loadError && dataSource?.length) {
        <div class="table-scroll-wrapper">
          <table [dataSource]="dataSource" class="mat-elevation-z8 table" mat-table>
            <ng-container matColumnDef="truckId">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Truck ID</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="text-type-8">{{ row?.values?.truck_id || '(no truck id)' }}</div>
                @if (row?.values?.truck_description) {
                  <div class="text-type-14 secondary-text">{{ row.values.truck_description }}</div>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="truckLicense">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Truck License</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="text-type-11">{{ row?.values?.truck_license || '-' }}</div>
                @if (row?.values?.trailer_license) {
                  <div class="text-type-14 secondary-text">Trailer {{ row.values.trailer_license }}</div>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="vendor">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Vendor</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="text-type-11">{{ vendorName(row) || '-' }}</div>
              </td>
            </ng-container>

            <ng-container matColumnDef="configuration">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Configuration</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="text-type-11">{{ row?.values?.configuration || '-' }}</div>
              </td>
            </ng-container>

            <ng-container matColumnDef="allowableLegal">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Allowable Legal (lb)</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="text-type-11">
                  {{ row?.values?.allowable_legal !== null && row?.values?.allowable_legal !== undefined && row?.values?.allowable_legal !== ''
                    ? (row.values.allowable_legal | number : '1.0-0')
                    : '-' }}
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="status">
              <th *matHeaderCellDef mat-header-cell>
                <span class="th-text">Status</span>
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="status-chip-container flex flex-col items-start gap-[4px]">
                  <div
                    [ngClass]="[statusMap[statusKey(row)].bgColor, statusMap[statusKey(row)].textColor]"
                    class="status-chip px-[14px] rounded-full"
                    >
                    {{ statusMap[statusKey(row)].label }}
                  </div>
                  @if (statusDate(row)) {
                    <div class="text-type-14 secondary-text">{{ statusDate(row) }}</div>
                  }
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th *matHeaderCellDef mat-header-cell></th>
              <td *matCellDef="let row" mat-cell class="actions-cell">
                <button
                  [matMenuTriggerFor]="rowMenu"
                  (click)="$event.stopPropagation()"
                  aria-label="MGVW record actions"
                  class="menu-button"
                  mat-icon-button
                  >
                  <mat-icon>more_horiz</mat-icon>
                </button>
                <mat-menu #rowMenu="matMenu">
                  <button (click)="viewPdf(row, $event)" mat-menu-item>
                    <mat-icon>download</mat-icon>
                    <span>Download PDF</span>
                  </button>
                  @if (canDeleteRow(row)) {
                    <button (click)="deleteRecord(row, $event)" mat-menu-item class="text-red-500">
                      <mat-icon svgIcon="trash" class="text-red-500"></mat-icon>
                      <span>Delete</span>
                    </button>
                  }
                </mat-menu>
              </td>
            </ng-container>

            <tr *matHeaderRowDef="displayedColumns" mat-header-row></tr>
            <tr
              data-testid="mgvw-record-row"
              (click)="rowClicked(row)"
              *matRowDef="let row; columns: displayedColumns"
              [class.highlighted-row]="row === selectedRow"
              mat-row
              ></tr>
          </table>
        </div>
      }

      @if (!loading && !loadError && !dataSource?.length) {
        <div class="no-data text-type-8 mt-[80px]">
          {{ allRecords.length ? 'No MGVW records match your search.' : 'No MGVW records for this project yet.' }}
        </div>
      }

      <app-paginator
        (pageChanged)="onPageChanged($event)"
        [itemsPerPage]="itemsPerPage"
        [maxVisiblePages]="5"
        [totalItems]="totalRecords"
        class="paginator"
        />
    </div>
  </div>
</div>
